'use client';

/**
 * CustomProductsExplorerCard — the buyer's "Custom Products": their BW_ products
 * (WANTS-owned), rendered as a CATEGORY-BROWSE tree below "My Favourites" in mode11.
 *
 * Uses the SAME `category-tree-shell` primitive as the catalogue browse
 * (CategoryTreeCard), so the two trees in the rail speak one visual + interaction
 * language: each category shelf (table / seating / …) is a SELECTABLE bucket row
 * (identical look), clicking it scopes the canvas to that category's custom
 * products (custom_products_grid reads layout-store.customProductCategory). The
 * product leaves are child rows → inspectEntity (rail brief, canvas stays). This
 * REPLACES the old ProductTierRow rendering (which had non-selectable uppercase
 * shelf labels and its own typography — the drift this unification kills).
 *
 * A custom product is a PT_-shaped PRODUCT whose owner edge is WANTS (buyer)
 * rather than SUPPLIES (vendor). The BFF (`catalog_list_custom_products`) delivers
 * them already grouped (the assembler's category shelves) — the frontend PAINTS.
 *
 * CRUD (all CO_/TT_-scoped, BW_-keyed):
 *   - Create → "+" → add_wish form (create mode)
 *   - Edit   → leaf kebab → setEditSid + add_wish (edit mode) → catalog_update_custom_product
 *   - Remove → leaf kebab → confirm → catalog_delete_custom_product (detaches WANTS)
 *
 * @see klyde_edge/ui_config/handlers/catalog.py — catalog_list_custom_products
 * @see ../search/category-tree-shell.tsx — the shared browse-tree primitive
 * @see ../search/category-tree-card.tsx — the catalogue-browse twin
 * @see ../../../canvas/cards/products/custom-products-grid-card.tsx — the canvas grid it scopes
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useMemo, useState } from 'react';
import { Boxes, MoreVertical, Pencil, Trash2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useStrategyData } from '@/lib/api/hooks';
import { api } from '@/lib/api/client';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useCustomProductEditStore } from '@/lib/stores/custom-product-edit-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { CategoryTreeFrame, CategoryBucketRow, CategoryChildRow } from '../search/category-tree-shell';

/** The standard product shape catalog_list_custom_products returns. */
interface CustomProductCard {
  semantic_id: string;
  name: string;
  list_price: number | null;
  image_url: string | null;
  category: string | null;
  saved: boolean;
}
interface CustomProductsResponse {
  groups: { label: string; count: number; products: CustomProductCard[] }[];
  ungrouped: CustomProductCard[];
  count: number;
}

/** Sentinel category for the "Uncategorised" bucket (products with no category);
 *  custom_products_grid matches on it to show the ungrouped slice. */
const UNGROUPED = '__ungrouped__';

export function CustomProductsExplorerCard() {
  const label = useProductLabel('custom_products_group');
  const uncatLabel = useProductLabel('custom_products_uncategorised');
  const addLabel = useProductLabel('custom_products_add');
  const menuLabel = useProductLabel('custom_products_menu');
  const editLabel = useProductLabel('custom_products_edit');
  const removeLabel = useProductLabel('custom_products_remove');
  const removedLabel = useProductLabel('custom_products_removed');
  const removeConfirmLabel = useProductLabel('custom_products_remove_confirm');

  const listStrategy = useCapabilityStore((s) => s.strategyName('catalog', 'list_custom_products'));
  const canCreate = useCapabilityStore((s) => s.strategyName('catalog', 'create_wish') !== null);
  const updateStrategy = useCapabilityStore((s) => s.strategyName('catalog', 'update_custom_product'));
  const deleteStrategy = useCapabilityStore((s) => s.strategyName('catalog', 'delete_custom_product'));

  // Leaf = a PRODUCT → inspectEntity (rail brief, canvas stays). Category bucket
  // = a scope → flip the canvas to custom_products_grid filtered to that category.
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const inspectedEntity = useGraphSelectionStore((s) => s.inspectedEntity);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const explorerSelection = useLayoutStore((s) => s.explorerSelection);
  const activeCategory = useLayoutStore((s) => s.customProductCategory);
  const setCustomProductCategory = useLayoutStore((s) => s.setCustomProductCategory);
  const setEditSid = useCustomProductEditStore((s) => s.setEditSid);
  const queryClient = useQueryClient();

  const { data } = useStrategyData<CustomProductsResponse>(listStrategy ?? '', {}, !!listStrategy);
  const res = data?.data;
  const groups = res?.groups ?? [];
  const ungrouped = res?.ungrouped ?? [];
  const count = res?.count ?? 0;

  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggle = (k: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(k) ? next.delete(k) : next.add(k);
      return next;
    });

  const listQueryKey = useMemo(() => ['strategy-data', listStrategy ?? '', {}] as const, [listStrategy]);
  useCardExport(useMemo(() => ({
    label,
    content_key: 'custom_products_explorer',
    surface: 'explorer' as const,
    status: (res ? 'loaded' : 'loading') as 'loading' | 'loaded',
    query_key: listQueryKey,
    text: `${label} — ${count}`,
    json: { count, groups: groups.map((g) => ({ label: g.label, count: g.count })), ungrouped: ungrouped.length },
  }), [label, res, count, groups, ungrouped, listQueryKey]));

  // Render when the buyer HAS custom products OR may CREATE them.
  if (count === 0 && !canCreate) return null;

  const refresh = () => queryClient.invalidateQueries({ queryKey: listQueryKey });
  // "+" → add_wish in CREATE mode (clear any stale edit target).
  const openAdd = () => {
    setEditSid(null);
    setExplorerSelection('add_wish');
  };
  const openEdit = (id: string) => {
    setEditSid(id);
    setExplorerSelection('add_wish');
  };
  const removeLeaf = async (id: string, name: string) => {
    if (!deleteStrategy) return;
    let r;
    try {
      r = await api.getStrategyData<{ success: boolean; error?: string }>(deleteStrategy, { product_sid: id });
    } catch (err) {
      toast.error(`${removeLabel} failed: ${err instanceof Error ? err.message : 'network error'}`);
      return;
    }
    if (r.success && r.data?.success) {
      refresh();
      toast.success(`${removedLabel}: ${name}`);
    } else {
      toast.error(r.data?.error ?? r.error ?? `${removeLabel} failed`);
    }
  };

  // Category bucket click → scope the canvas to that category (flip to the grid).
  const isCanvasActive = explorerSelection === 'custom_products';
  const pickCategory = (categoryKey: string) => {
    setCustomProductCategory(categoryKey);
    setExplorerSelection('custom_products');
  };
  // Expanding the top-level header → paint ALL the (valid) categories on the
  // canvas as collection tiles: select custom_products with no category scope.
  const pickAll = () => {
    setCustomProductCategory(null);
    setExplorerSelection('custom_products');
  };

  const canEdit = !!updateStrategy;
  const canDelete = !!deleteStrategy;
  const showKebab = canEdit || canDelete;

  const renderLeaf = (p: CustomProductCard) => (
    <CategoryChildRow
      key={p.semantic_id}
      label={p.name}
      capitalize={false}
      active={inspectedEntity?.id === p.semantic_id}
      onClick={() =>
        inspectEntity({
          id: p.semantic_id,
          label: p.name,
          type: 'product',
          properties: { list_price: p.list_price, image_url: p.image_url },
        })
      }
      trailing={
        <div className="flex items-center gap-1 shrink-0">
          {p.list_price != null && (
            <span className={cn(text.muted, 'shrink-0')}>${p.list_price.toLocaleString()}</span>
          )}
          {showKebab && (
            <CustomProductLeafMenu
              menuLabel={menuLabel}
              editLabel={editLabel}
              removeLabel={removeLabel}
              removeConfirmLabel={removeConfirmLabel}
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => openEdit(p.semantic_id)}
              onRemove={() => void removeLeaf(p.semantic_id, p.name)}
            />
          )}
        </div>
      }
    />
  );

  const bucket = (key: string, lbl: string, products: CustomProductCard[]) => (
    <CategoryBucketRow
      key={key}
      label={lbl}
      count={products.length}
      countSuffix=""
      active={isCanvasActive && activeCategory === key}
      expandable={products.length > 0}
      expanded={expanded.has(key)}
      onToggleExpand={() => toggle(key)}
      onPick={() => pickCategory(key)}
    >
      {products.map(renderLeaf)}
    </CategoryBucketRow>
  );

  return (
    <CategoryTreeFrame
      title={label}
      icon={Boxes}
      iconClassName="text-amber-500"
      onAdd={canCreate ? openAdd : undefined}
      addLabel={addLabel}
      collapsible
      defaultCollapsed
      count={count}
      onSelect={pickAll}
      selected={isCanvasActive && activeCategory === null}
    >
      {groups.map((g) => bucket(g.label, g.label, g.products))}
      {ungrouped.length > 0 && bucket(UNGROUPED, uncatLabel, ungrouped)}
    </CategoryTreeFrame>
  );
}

/** Per-leaf Edit / Remove kebab. Remove uses an inline two-step confirm (no
 *  modal) — first click arms it, second click detaches. */
function CustomProductLeafMenu({
  menuLabel,
  editLabel,
  removeLabel,
  removeConfirmLabel,
  canEdit,
  canDelete,
  onEdit,
  onRemove,
}: {
  menuLabel: string;
  editLabel: string;
  removeLabel: string;
  removeConfirmLabel: string;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const close = () => {
    setOpen(false);
    setConfirming(false);
  };

  return (
    <div className="relative shrink-0">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setConfirming(false);
          setOpen((v) => !v);
        }}
        aria-label={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors opacity-0 group-hover/leaf:opacity-100 aria-expanded:opacity-100"
      >
        <MoreVertical className="h-3.5 w-3.5" />
      </button>

      {open && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={(e) => {
              e.stopPropagation();
              close();
            }}
            aria-hidden="true"
          />
          <div
            role="menu"
            className="absolute right-0 top-7 z-50 w-40 rounded-md border border-border bg-popover shadow-md py-1"
          >
            {canEdit && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  close();
                  onEdit();
                }}
                className={cn(text.body, 'w-full flex items-center gap-2 px-3 py-1.5 hover:bg-accent text-left')}
              >
                <Pencil className="w-3.5 h-3.5" />
                {editLabel}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                role="menuitem"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!confirming) {
                    setConfirming(true);
                    return;
                  }
                  close();
                  onRemove();
                }}
                className={cn(
                  text.body,
                  'w-full flex items-center gap-2 px-3 py-1.5 text-left text-destructive',
                  confirming ? 'bg-destructive/10 font-medium' : 'hover:bg-accent',
                )}
              >
                <Trash2 className="w-3.5 h-3.5" />
                {confirming ? removeConfirmLabel : removeLabel}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
